import store, { AppDispatch } from 'core/store';
import {
  AnalyzeProductImageResult,
  isEmptyOcrValue
} from 'core/hooks/useAnalyzeProductImage';
import { UpdateProductBody } from 'core/types';
import {
  searchManufacturersForOcr,
  searchProductModelsForOcr
} from 'features/shared/inventory/store/InventoryActions';

export interface AnalyzedProductUpdate {
  body: UpdateProductBody;
  appliedLabels: string[];
  unmatchedNotes: string[];
}

const normalize = (value: string) => value.trim().toLowerCase();

/**
 * Turns an OCR result into a PATCH body, resolving the free-text
 * manufacturer/model guesses against the catalog search endpoints rather than
 * applying them as-is — there's nowhere to store free text, Brand/Model # are
 * expressed by repointing `manufacturer_id`/`product_template_id` at a real
 * catalog row (see design.md D2/D10/D11).
 *
 * Only applies a resolved id on an exact (case-insensitive) name match.
 * `_manufacturer_search_query`/`_product_model_search_query` order results
 * alphabetically, not by relevance, so treating "the first result" as the
 * best match on a partial/fuzzy hit risks silently applying the wrong
 * brand/model to a real product. An unmatched guess is surfaced via
 * `unmatchedNotes` instead, extending the app's existing "detected model
 * number but couldn't apply it" toast pattern to manufacturer too.
 *
 * Model # wins over Brand/Manufacturer when both resolve (repointing
 * `product_template_id` is the more specific selection, matching D10's
 * existing PATCH precedence); a resolved Brand alone still applies via
 * `manufacturer_id`, reusing the "Default/Generic" model mechanism (D10).
 */
export const resolveAnalyzedProductUpdate = async (
  dispatch: AppDispatch,
  homeId: number,
  typeId: number,
  result: AnalyzeProductImageResult
): Promise<AnalyzedProductUpdate> => {
  const body: UpdateProductBody = {};
  const appliedLabels: string[] = [];
  const unmatchedNotes: string[] = [];

  if (!isEmptyOcrValue(result.serial_number)) {
    body.serial = result.serial_number.trim();
    appliedLabels.push('Serial #');
  }

  const manufacturerGuess = !isEmptyOcrValue(result.manufacturer)
    ? result.manufacturer
    : result.product_manufacturer_name;
  let resolvedManufacturerId: number | undefined;
  if (!isEmptyOcrValue(manufacturerGuess)) {
    await dispatch(searchManufacturersForOcr(typeId, manufacturerGuess));
    const match = store
      .getState()
      .inventory.ocrManufacturers.find(
        (candidate) =>
          normalize(candidate.name) === normalize(manufacturerGuess)
      );
    if (match) {
      resolvedManufacturerId = match.id;
    } else {
      unmatchedNotes.push(`brand "${manufacturerGuess}"`);
    }
  }

  const modelGuess = !isEmptyOcrValue(result.model_number)
    ? result.model_number
    : result.model_name;
  let modelApplied = false;
  if (!isEmptyOcrValue(modelGuess)) {
    await dispatch(
      searchProductModelsForOcr(
        homeId,
        modelGuess,
        typeId,
        resolvedManufacturerId
      )
    );
    const match = store
      .getState()
      .inventory.ocrProductModels.find(
        (candidate) => normalize(candidate.model) === normalize(modelGuess)
      );
    if (match) {
      body.product_template_id = match.id;
      appliedLabels.push('Model #');
      modelApplied = true;
    } else {
      unmatchedNotes.push(`model number "${modelGuess}"`);
    }
  }

  if (!modelApplied && resolvedManufacturerId !== undefined) {
    body.manufacturer_id = resolvedManufacturerId;
    appliedLabels.push('Brand/Manufacturer');
  }

  return { body, appliedLabels, unmatchedNotes };
};
